import type { MediaItemInterface } from '@hypercodestudio/basler-components';

import { getMediaPreferredImageSrc } from '~/utils/mediaImageSrc';

/**
 * Preloads an image URL so the browser has it decoded before swapping from blur preview.
 * Resolves with false on error or missing src so callers can keep showing the preview.
 */
export function preloadImage(src: string): Promise<boolean> {
  if (typeof window === 'undefined' || !src) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const image = new Image();

    const finish = (loaded: boolean): void => {
      resolve(loaded);
    };

    image.onload = () => {
      if (typeof image.decode === 'function') {
        void image
          .decode()
          .then(() => finish(true))
          .catch(() => finish(true));
        return;
      }
      finish(true);
    };
    image.onerror = () => finish(false);
    image.src = src;
  });
}

/**
 * Tries AVIF first, then WebP, then the largest available URL.
 * Replaces client-side format probing: unsupported formats fail fast via onerror.
 */
export async function preloadMediaHighRes(
  media: MediaItemInterface | undefined
): Promise<boolean> {
  if (!media || media.type !== 'image') {
    return false;
  }

  const avifSrc = getMediaPreferredImageSrc(media, 'avif');
  if (avifSrc && (await preloadImage(avifSrc))) {
    return true;
  }

  const webpSrc = getMediaPreferredImageSrc(media, 'webp');
  if (webpSrc && (await preloadImage(webpSrc))) {
    return true;
  }

  const fallbackSrc = getMediaPreferredImageSrc(media);
  return Boolean(fallbackSrc && (await preloadImage(fallbackSrc)));
}
